import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { CONFIG } from "../config";
import axios from "axios";
import CognitiveRadarChart from "../components/CognitiveRadarChart";
import SocialCognitionScatterChart from "../components/SocialCognitionScatterChart";
import {
  buildCognitiveRadarProfile,
  countRadarDataPoints,
} from "../utils/patientCognitiveRadar";
import { buildSocialCognitionScatter } from "../utils/patientSocialCognitionScatter";
import { buildBeckAffectiveSpectrum } from "../utils/patientBeckAffectiveSpectrum";
import BeckAffectiveSpectrumChart from "../components/BeckAffectiveSpectrumChart";
import CriticalItemsBreakdownChart from "../components/CriticalItemsBreakdownChart";
import ExpedientesCompareView from "../components/ExpedientesCompareView";
import TestScoreTrendChart from "../components/TestScoreTrendChart";
import { buildCriticalItemsBreakdown } from "../utils/patientCriticalItems";

import "../css/expedientes.css";

export default function Expedientes() {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("administradas");
  const [compareSelection, setCompareSelection] = useState([]);
  const [comparePatients, setComparePatients] = useState(null);
  const [registerOpen, setRegisterOpen] = useState(false);

  const cognitiveRadarProfile = useMemo(
    () => buildCognitiveRadarProfile(selectedPatient?.tests || []),
    [selectedPatient]
  );

  const radarDataCount = useMemo(
    () => countRadarDataPoints(cognitiveRadarProfile),
    [cognitiveRadarProfile]
  );

  const socialCognitionScatter = useMemo(
    () => buildSocialCognitionScatter(selectedPatient?.tests || []),
    [selectedPatient]
  );

  const beckAffectiveSpectrum = useMemo(
    () => buildBeckAffectiveSpectrum(selectedPatient?.tests || []),
    [selectedPatient]
  );

  const criticalItemsBreakdown = useMemo(
    () => buildCriticalItemsBreakdown(selectedPatient?.tests || []),
    [selectedPatient]
  );

  const fetchPatients = useCallback(async ({ initial = false } = {}) => {
    if (initial) setLoading(true);
    try {
      const res = await axios.get(
        `${CONFIG.BACKEND_URL}/backend/getpatientswithtests`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setPatients(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      if (error.response?.status === 404) {
        setPatients([]);
      } else {
        console.error("Error al cargar pacientes:", error);
      }
    } finally {
      if (initial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients({ initial: true });
  }, [fetchPatients]);

  const filteredPatients = useMemo(() => {
    if (!search.trim()) return patients;
    const q = search.toLowerCase().trim();
    return patients.filter(
      (p) =>
        p.name &&
        p.name.toLowerCase().includes(q)
    );
  }, [patients, search]);

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

  const formatTime = (seconds) => {
    if (seconds == null || seconds === undefined) return "—";
    const h = Math.floor(Number(seconds) / 3600);
    const m = Math.floor((Number(seconds) % 3600) / 60);
    const s = Math.floor(Number(seconds) % 60);
    return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
  };

  const getPatientLastDate = (patient) => {
    const tests = patient.tests || [];
    if (tests.length === 0) return { date: null, time: null };
    const sorted = [...tests].sort(
      (a, b) => new Date(b.testDay) - new Date(a.testDay)
    );
    const last = sorted[0];
    return { date: last.testDay, time: last.time };
  };

  const toggleCompareSelection = (patientId, event) => {
    event.stopPropagation();
    setCompareSelection((current) => {
      if (current.includes(patientId)) {
        return current.filter((id) => id !== patientId);
      }
      if (current.length >= 2) {
        return [current[1], patientId];
      }
      return [...current, patientId];
    });
  };

  const handleCompare = () => {
    if (compareSelection.length !== 2) return;

    const selected = compareSelection
      .map((id) => patients.find((patient) => patient.id === id))
      .filter(Boolean);

    if (selected.length !== 2) return;

    setComparePatients(selected);
    setSelectedPatient(null);
    setActiveTab("dashboard");
  };

  const exitCompareMode = () => {
    setComparePatients(null);
    setCompareSelection([]);
  };

  const handleSelectPatient = (patient) => {
    setComparePatients(null);
    setSelectedPatient(patient);
  };

  const isCompareMode = comparePatients?.length === 2;
  const canCompare = compareSelection.length === 2;

  if (loading) {
    return (
      <div className="expedientes-page expedientes-page--loading">
        <div className="expedientes-loading">Cargando expedientes...</div>
      </div>
    );
  }

  const hasSelection = !!selectedPatient || isCompareMode;

  return (
    <div className={`expedientes-page ${hasSelection ? "expedientes-page--with-detail" : ""} ${isCompareMode ? "expedientes-page--compare" : ""}`}>
      {/* Panel izquierdo: lista de expedientes */}
      <div className="expedientes-list-panel">
        <header className="expedientes-header">
          <h1 className="expedientes-title">Expedientes</h1>
          <div className="expedientes-header-actions">
            <div className="expedientes-view-buttons">
              <button
                type="button"
                className="expedientes-view-btn expedientes-view-btn--list expedientes-view-btn--active"
                aria-label="Vista lista"
              >
                <ListIcon />
              </button>
              <button
                type="button"
                className="expedientes-view-btn expedientes-view-btn--filter"
                aria-label="Filtrar"
              >
                <FilterIcon />
              </button>
              <button
                type="button"
                className={`expedientes-compare-btn ${canCompare ? "expedientes-compare-btn--ready" : ""}`}
                disabled={!canCompare}
                onClick={handleCompare}
              >
                Comparar
              </button>
              <button
                type="button"
                className="expedientes-add-btn"
                onClick={() => setRegisterOpen(true)}
                aria-label="Registrar paciente"
                title="Registrar paciente"
              >
                +
              </button>
            </div>
            <div className="expedientes-search-wrap">
              <span className="expedientes-search-icon" aria-hidden="true">
                <SearchIcon />
              </span>
              <input
                type="search"
                className="expedientes-search"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Buscar expedientes"
              />
            </div>
          </div>
        </header>

        <ul className="expedientes-list">
          {filteredPatients.map((patient) => {
            const { date, time } = getPatientLastDate(patient);
            const isActive = selectedPatient?.id === patient.id;
            const isChecked = compareSelection.includes(patient.id);
            return (
              <li key={patient.id} className="expedientes-list-item">
                <label className="expedientes-patient-check">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(event) => toggleCompareSelection(patient.id, event)}
                    aria-label={`Seleccionar ${patient.name} para comparar`}
                  />
                </label>
                <button
                  type="button"
                  className={`expedientes-patient-row ${isActive ? "expedientes-patient-row--active" : ""} ${isChecked ? "expedientes-patient-row--checked" : ""}`}
                  onClick={() => handleSelectPatient(patient)}
                >
                  <span className="expedientes-patient-icon" aria-hidden="true">
                    <FolderIcon />
                  </span>
                  <span className="expedientes-patient-name">
                    {patient.name || "Nombre Apellido_paterno Apellido_materno"}
                  </span>
                  <span className="expedientes-patient-meta">
                    <span className="expedientes-patient-date">{formatDate(date)}</span>
                    <span className="expedientes-patient-time">{formatTime(time)}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {filteredPatients.length === 0 && (
          <p className="expedientes-empty">No hay expedientes que coincidan con la búsqueda.</p>
        )}
      </div>

      {/* Panel derecho: comparación o detalle */}
      {isCompareMode && (
        <ExpedientesCompareView
          patientA={comparePatients[0]}
          patientB={comparePatients[1]}
          onClose={exitCompareMode}
          groupTestsByName={groupTestsByName}
          TestAccordion={TestAccordion}
        />
      )}

      {!isCompareMode && selectedPatient && (
        <div className="expedientes-detail-panel">
          <h2 className="expedientes-detail-name">{selectedPatient.name}</h2>

          <section className="expedientes-detail-info">
            <p><strong>Edad:</strong> {selectedPatient.age} años</p>
            <p><strong>Fecha de nacimiento:</strong> {formatDate(selectedPatient.birthDate)}</p>
            <p><strong>Sexo:</strong> {selectedPatient.gender}</p>
            <p><strong>ID:</strong> {selectedPatient.idhub || selectedPatient.id || "—"}</p>
            <p><strong>Años de escolaridad:</strong> {selectedPatient.escolaridad ?? "—"}</p>
            <p><strong>Lateralidad:</strong> {selectedPatient.lateralidad ?? "—"}</p>
          </section>

          <div className="expedientes-detail-tabs">
            <button
              type="button"
              className={`expedientes-tab ${activeTab === "administradas" ? "expedientes-tab--active" : ""}`}
              onClick={() => setActiveTab("administradas")}
            >
              Pruebas administradas
            </button>
            <button
              type="button"
              className={`expedientes-tab ${activeTab === "dashboard" ? "expedientes-tab--active" : ""}`}
              onClick={() => setActiveTab("dashboard")}
            >
              Dashboard único
            </button>
          </div>

          {activeTab === "administradas" && (
            <div className="expedientes-tests-accordions">
              {groupTestsByName(selectedPatient.tests || []).map((group, idx) => (
                <TestAccordion
                  key={group.testName}
                  group={group}
                  defaultOpen={idx === 0}
                />
              ))}
              {(!selectedPatient.tests || selectedPatient.tests.length === 0) && (
                <p className="expedientes-no-tests">No hay pruebas administradas.</p>
              )}
            </div>
          )}

          {activeTab === "por-aplicar" && (
            <p className="expedientes-por-aplicar-placeholder">
              Listado de pruebas por aplicar (en desarrollo).
            </p>
          )}

          {activeTab === "dashboard" && (
            <div className="expedientes-dashboard-panel">
              <header className="expedientes-dashboard-header">
                <h3 className="expedientes-dashboard-title">
                  Radar de perfil cognitivo-emocional integrado
                </h3>
                <p className="expedientes-dashboard-subtitle">
                  Porcentaje alcanzado en la última sesión de FAB, Hinting, Eyes,
                  Empathy, Beck Depresión, Beck Ansiedad, Yesavage y Barthel.
                </p>
              </header>

              {radarDataCount === 0 ? (
                <p className="expedientes-dashboard-empty">
                  Este paciente aún no tiene pruebas registradas para construir
                  el perfil integrado.
                </p>
              ) : (
                <CognitiveRadarChart profile={cognitiveRadarProfile} />
              )}

              <section className="expedientes-dashboard-block">
                <header className="expedientes-dashboard-header">
                  <h3 className="expedientes-dashboard-title">
                    Módulo de cognición social: empatía vs. decodificación expresiva
                  </h3>
                  <p className="expedientes-dashboard-subtitle">
                    Comparativo bivariado entre decodificación expresiva (Reading
                    the Mind in the Eyes, eje X) y mentalización verbal (Hinting
                    Test, eje Y). Contexto de empatía autorreportada (Empathy Test)
                    en el radar integrado.
                  </p>
                </header>

                <SocialCognitionScatterChart scatter={socialCognitionScatter} />
              </section>

              <section className="expedientes-dashboard-block">
                <header className="expedientes-dashboard-header">
                  <h3 className="expedientes-dashboard-title">
                    Espectro afectivo: comparativa Beck (Ansiedad vs. Depresión)
                  </h3>
                  <p className="expedientes-dashboard-subtitle">
                    Puntaje directo de la última sesión con líneas de corte Leve,
                    Moderado y Severo según baremos estándar de Beck.
                  </p>
                </header>

                <BeckAffectiveSpectrumChart spectrum={beckAffectiveSpectrum} />
              </section>

              <section className="expedientes-dashboard-block">
                <header className="expedientes-dashboard-header">
                  <h3 className="expedientes-dashboard-title">
                    Desglose cualitativo de preguntas críticas
                  </h3>
                  <p className="expedientes-dashboard-subtitle">
                    Detalle de resultlist por pregunta (respuesta seleccionada y
                    puntaje) para detectar preguntas con mayor carga clínica,
                    como ideación suicida o anhedonia en Beck Depresión.
                  </p>
                </header>

                <CriticalItemsBreakdownChart breakdown={criticalItemsBreakdown} />
              </section>
            </div>
          )}
        </div>
      )}

      {registerOpen && (
        <RegisterPatientModal
          onClose={() => setRegisterOpen(false)}
          onRegistered={() => fetchPatients()}
        />
      )}
    </div>
  );
}

const EMPTY_PATIENT_FORM = {
  name: "",
  birthdate: "",
  gender: "",
  laterality: "",
  educationYear: "",
  occupation: "",
  contact: "",
  contact_e: "",
};

const BIRTHDATE_MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const BIRTHDATE_WEEKDAYS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const BIRTHDATE_YEAR_PAGE = 16;

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isSameDay(left, right) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function isoToDisplay(isoDate) {
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return "";
  return `${day}/${month}/${year}`;
}

function dateToIso(date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function maskBirthdate(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean);
  return parts.join("/");
}

function displayToIso(text) {
  const match = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return "";
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return "";
  }
  const today = startOfDay(new Date());
  const oldest = new Date(today.getFullYear() - 130, today.getMonth(), today.getDate());
  if (date > today || date < oldest) return "";
  return dateToIso(date);
}

function buildMonthGrid(year, month) {
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];

  for (let index = 0; index < firstWeekday; index += 1) {
    cells.push({
      date: new Date(year, month, index - firstWeekday + 1),
      outside: true,
    });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ date: new Date(year, month, day), outside: false });
  }
  let extra = 1;
  while (cells.length % 7 !== 0) {
    cells.push({ date: new Date(year, month + 1, extra), outside: true });
    extra += 1;
  }
  return cells;
}

function BirthdateField({ value, onChange }) {
  const anchorRef = useRef(null);
  const popoverRef = useRef(null);
  const today = startOfDay(new Date());
  const selected = value ? startOfDay(new Date(`${value}T00:00:00`)) : null;
  const [text, setText] = useState(value ? isoToDisplay(value) : "");
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState("days");
  const [visibleMonth, setVisibleMonth] = useState(selected || today);
  const [coords, setCoords] = useState(null);

  const minYear = today.getFullYear() - 120;
  const yearPageStart = (() => {
    const year = Math.min(today.getFullYear(), Math.max(minYear, visibleMonth.getFullYear()));
    const indexFromCurrent = today.getFullYear() - year;
    return today.getFullYear() - Math.floor(indexFromCurrent / BIRTHDATE_YEAR_PAGE) * BIRTHDATE_YEAR_PAGE;
  })();

  useEffect(() => {
    if (!open) return undefined;

    const updatePosition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 318;
      const height = 390;
      let top = rect.bottom + 10;
      let left = rect.left;
      if (top + height > window.innerHeight - 8) {
        top = Math.max(8, rect.top - height - 10);
      }
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8);
      }
      setCoords({ top, left });
    };

    const onPointerDown = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (popoverRef.current?.contains(event.target)) return;
      setOpen(false);
    };

    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open, panel]);

  const commitDate = (date) => {
    const iso = dateToIso(date);
    setText(isoToDisplay(iso));
    onChange(iso);
    setVisibleMonth(date);
    setPanel("days");
    setOpen(false);
  };

  const handleText = (event) => {
    const next = maskBirthdate(event.target.value);
    setText(next);
    const iso = displayToIso(next);
    onChange(iso);
    if (iso) setVisibleMonth(startOfDay(new Date(`${iso}T00:00:00`)));
  };

  const shiftMonth = (amount) => {
    const next = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + amount, 1);
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    if (next > currentMonth) return;
    setVisibleMonth(next);
  };

  const shiftYearPage = (amount) => {
    const nextStart = yearPageStart + amount * BIRTHDATE_YEAR_PAGE;
    if (nextStart > today.getFullYear() || nextStart < minYear) return;
    const month = nextStart === today.getFullYear()
      ? Math.min(visibleMonth.getMonth(), today.getMonth())
      : visibleMonth.getMonth();
    setVisibleMonth(new Date(nextStart, month, 1));
  };

  const cells = buildMonthGrid(visibleMonth.getFullYear(), visibleMonth.getMonth());
  const years = Array.from({ length: BIRTHDATE_YEAR_PAGE }, (_, index) => yearPageStart - index)
    .filter((year) => year >= minYear && year <= today.getFullYear());
  const canGoNextMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1)
    <= new Date(today.getFullYear(), today.getMonth(), 1);
  const canGoNewerYears = yearPageStart < today.getFullYear();
  const canGoOlderYears = yearPageStart - BIRTHDATE_YEAR_PAGE >= minYear;

  return (
    <div className="birthdate-field" ref={anchorRef}>
      <input
        type="text"
        inputMode="numeric"
        placeholder="DD/MM/AAAA"
        value={text}
        onChange={handleText}
        onFocus={() => {
          setPanel("days");
          setOpen(true);
        }}
        aria-label="Fecha de nacimiento"
        autoComplete="bday"
        required
      />
      <button
        type="button"
        className="birthdate-open"
        aria-label="Abrir calendario"
        onClick={() => {
          setPanel("days");
          setOpen((current) => !current);
        }}
      >
        <CalendarIcon />
      </button>
      {open && coords && createPortal(
        <div
          className="birthdate-popover"
          ref={popoverRef}
          style={{ top: coords.top, left: coords.left }}
        >
          <div className="birthdate-sheet birthdate-sheet--back" />
          <div className="birthdate-sheet birthdate-sheet--mid" />
          <div className="birthdate-calendar" role="dialog" aria-label="Calendario">
            <div className="birthdate-header">
              <button
                type="button"
                className="birthdate-nav"
                aria-label={panel === "days" ? "Mes anterior" : "Años anteriores"}
                onClick={() => (panel === "days" ? shiftMonth(-1) : shiftYearPage(-1))}
                disabled={panel === "years" && !canGoOlderYears}
              >
                <BirthdateChevronLeftIcon />
              </button>
              <button
                type="button"
                className="birthdate-title"
                onClick={() => setPanel((current) => (current === "days" ? "years" : "days"))}
              >
                {panel === "days"
                  ? `${BIRTHDATE_MONTHS[visibleMonth.getMonth()]} ${visibleMonth.getFullYear()}`
                  : `${years[years.length - 1]} – ${years[0]}`}
              </button>
              <button
                type="button"
                className="birthdate-nav"
                aria-label={panel === "days" ? "Mes siguiente" : "Años siguientes"}
                onClick={() => (panel === "days" ? shiftMonth(1) : shiftYearPage(1))}
                disabled={panel === "days" ? !canGoNextMonth : !canGoNewerYears}
              >
                <BirthdateChevronRightIcon />
              </button>
            </div>

            {panel === "days" ? (
              <>
                <div className="birthdate-week">
                  {BIRTHDATE_WEEKDAYS.map((weekday) => (
                    <span key={weekday}>{weekday}</span>
                  ))}
                </div>
                <div className="birthdate-grid">
                  {cells.map(({ date, outside }) => {
                    const future = startOfDay(date) > today;
                    const selectedDay = selected && isSameDay(date, selected);
                    const todayDay = isSameDay(date, today);
                    return (
                      <button
                        key={dateToIso(date)}
                        type="button"
                        className={[
                          "birthdate-day",
                          outside ? "birthdate-day--outside" : "",
                          selectedDay ? "birthdate-day--selected" : "",
                          todayDay ? "birthdate-day--today" : "",
                        ].filter(Boolean).join(" ")}
                        disabled={future}
                        onClick={() => commitDate(date)}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="birthdate-years">
                {years.map((year) => (
                  <button
                    key={year}
                    type="button"
                    className={`birthdate-year ${year === visibleMonth.getFullYear() ? "birthdate-year--selected" : ""}`}
                    onClick={() => {
                      const month = Math.min(visibleMonth.getMonth(), year === today.getFullYear() ? today.getMonth() : 11);
                      setVisibleMonth(new Date(year, month, 1));
                      setPanel("days");
                    }}
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 3.5v3M16 3.5v3M3.5 9.5h17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function BirthdateChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
    </svg>
  );
}

function BirthdateChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6z" />
    </svg>
  );
}

function calculateAge(isoDate) {
  const [year, month, day] = isoDate.split("-").map(Number);
  if (!year || !month || !day) return null;
  const today = new Date();
  let age = today.getFullYear() - year;
  const monthDiff = today.getMonth() + 1 - month;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
    age -= 1;
  }
  return age;
}

function RegisterPatientModal({ onClose, onRegistered }) {
  const [form, setForm] = useState(EMPTY_PATIENT_FORM);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, submitting]);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const token = localStorage.getItem("token");
    if (!token) {
      setError("Inicia sesión para registrar pacientes.");
      return;
    }

    const name = form.name.trim();
    const occupation = form.occupation.trim();
    const contact = form.contact.trim();
    const contact_e = form.contact_e.trim();
    const educationYear = Number(form.educationYear);
    const age = calculateAge(form.birthdate);

    if (!name) {
      setError("Escribe el nombre completo.");
      return;
    }
    if (!form.birthdate || age == null || age < 0 || age > 130) {
      setError("Indica una fecha de nacimiento válida.");
      return;
    }
    if (!form.gender) {
      setError("Selecciona el sexo.");
      return;
    }
    if (!form.laterality) {
      setError("Selecciona la lateralidad.");
      return;
    }
    if (!Number.isInteger(educationYear) || educationYear < 0 || educationYear > 40) {
      setError("Indica los años de escolaridad con un número entre 0 y 40.");
      return;
    }
    if (!occupation) {
      setError("Escribe la ocupación.");
      return;
    }
    if (contact && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact)) {
      setError("El contacto debe ser un correo válido.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${CONFIG.BACKEND_URL}/backend/registerpatient`,
        {
          name,
          age,
          gender: form.gender,
          birthdate: form.birthdate,
          laterality: form.laterality,
          educationYear,
          occupation,
          contact,
          contact_e,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      await onRegistered();
      onClose();
    } catch (submitError) {
      const message = submitError.response?.data;
      const knownMessages = {
        "Error registering patient": "No se pudo registrar el paciente.",
        "Invalid user token": "La sesión no es válida. Vuelve a iniciar sesión.",
        Forbidden: "La sesión no es válida. Vuelve a iniciar sesión.",
        Unauthorized: "Inicia sesión para registrar pacientes.",
      };
      setError(
        typeof message === "string" && message
          ? knownMessages[message] || message
          : "No se pudo registrar el paciente. Intenta de nuevo."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="expedientes-modal-backdrop"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className="expedientes-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="register-patient-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="expedientes-modal-header">
          <h2 id="register-patient-title" className="expedientes-modal-title">
            Registrar paciente
          </h2>
          <button
            type="button"
            className="expedientes-modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Cerrar"
          >
            ×
          </button>
        </header>

        <form className="expedientes-modal-form" onSubmit={handleSubmit}>
          <div className="expedientes-modal-fields">
          <label className="expedientes-field">
            <span>Nombre completo</span>
            <input
              type="text"
              value={form.name}
              onChange={updateField("name")}
              placeholder="Nombre completo del registro"
              autoFocus
              required
            />
          </label>

          <label className="expedientes-field">
            <span>Fecha de nacimiento</span>
            <BirthdateField
              value={form.birthdate}
              onChange={(birthdate) => setForm((current) => ({ ...current, birthdate }))}
            />
          </label>

          <label className="expedientes-field">
            <span>Sexo</span>
            <select value={form.gender} onChange={updateField("gender")} required>
              <option value="">Seleccione sexo</option>
              <option value="H">Hombre</option>
              <option value="M">Mujer</option>
              <option value="Prefiero no decir">Prefiero no decir</option>
            </select>
          </label>

          <label className="expedientes-field">
            <span>Lateralidad</span>
            <select value={form.laterality} onChange={updateField("laterality")} required>
              <option value="">Seleccione lateralidad</option>
              <option value="Der">Derecho</option>
              <option value="Izq">Izquierdo</option>
            </select>
          </label>

          <label className="expedientes-field">
            <span>Años de escolaridad (desde 1° de primaria)</span>
            <input
              type="number"
              min="0"
              max="40"
              step="1"
              value={form.educationYear}
              onChange={updateField("educationYear")}
              placeholder="Años de escolaridad"
              required
            />
          </label>

          <label className="expedientes-field">
            <span>Ocupación</span>
            <input
              type="text"
              value={form.occupation}
              onChange={updateField("occupation")}
              placeholder="Ocupación del paciente"
              required
            />
          </label>

          <label className="expedientes-field">
            <span>Contacto (opcional)</span>
            <input
              type="email"
              value={form.contact}
              onChange={updateField("contact")}
              placeholder="Correo"
            />
          </label>

          <label className="expedientes-field">
            <span>Contacto de emergencia (opcional)</span>
            <input
              type="tel"
              value={form.contact_e}
              onChange={updateField("contact_e")}
              placeholder="Teléfono"
            />
          </label>
          </div>

          {error && (
            <p className="expedientes-modal-error" role="alert">
              {error}
            </p>
          )}

          <div className="expedientes-modal-actions">
            <button
              type="button"
              className="expedientes-modal-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="expedientes-modal-submit"
              disabled={submitting}
            >
              {submitting ? "Registrando..." : "Registrar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function groupTestsByName(tests) {
  const grouped = tests.reduce((acc, test) => {
    const name = test.testName || "Sin nombre";
    acc[name] = acc[name] || [];
    acc[name].push(test);
    return acc;
  }, {});
  return Object.entries(grouped).map(([testName, tests]) => ({
    testName,
    tests,
  }));
}

function getTestIdLabel(testName, index) {
  const initials = (testName || "")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "XX";
  return `${initials}${String(index + 1).padStart(3, "0")}`;
}

function getScoreLabel(testResultNumber, result) {
  if (testResultNumber != null && testResultNumber !== "") return String(testResultNumber);
  if (result != null && result !== "") return String(result);
  return "—";
}

function TestAccordion({ group, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const maxScore = 36;

  return (
    <div className="expedientes-accordion">
      <button
        type="button"
        className="expedientes-accordion-header"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="expedientes-accordion-title">{group.testName}</span>
        <span className="expedientes-accordion-count">
          Pruebas realizadas: {group.tests.length}
        </span>
        <span className="expedientes-accordion-chevron" aria-hidden="true">
          {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
        </span>
      </button>
      {open && (
        <div className="expedientes-accordion-body">
          <TestScoreTrendChart testName={group.testName} tests={group.tests} />
          <div className="expedientes-table-wrap">
            <table className="expedientes-table">
              <thead>
                <tr>
                  <th>ID Prueba</th>
                  <th>Puntuación /{maxScore}</th>
                  <th>Fecha de aplicación</th>
                  <th>Duración</th>
                </tr>
              </thead>
              <tbody>
                {group.tests.map((test, index) => (
                  <tr key={index}>
                    <td>{getTestIdLabel(group.testName, index)}</td>
                    <td>{getScoreLabel(test.testResultNumber, test.result)}</td>
                    <td>{new Date(test.testDay).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" })}</td>
                    <td>{formatTimeSeconds(test.time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function formatTimeSeconds(seconds) {
  if (seconds == null || seconds === undefined) return "—";
  const n = Number(seconds);
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = Math.floor(n % 60);
  return [h, m, s].map((x) => String(x).padStart(2, "0")).join(":");
}

function ListIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 5h2v2H3V5zm4 0h14v2H7V5zm-4 6h2v2H3v-2zm4 0h14v2H7v-2zm-4 6h2v2H3v-2zm4 0h14v2H7v-2z" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M7 10l5 5 5-5z" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
    </svg>
  );
}
